<template>
  <div>
    <b-modal
      ref="modalSyncVariantKompack"
      size="lg"
      dialog-class="!max-w-[600px]"
      body-class="!p-[32px]"
      static
      hide-footer
      hide-header
      centered
    >
      <b-button
        variant="ghost"
        class="absolute top-[16px] right-[16px] !p-[16px] [&.btn-danger]:!bg-[#E31A1A] !flex items-center justify-center gap-[8px]"
        @click="closeModal"
      >
        <span class="k-close-circle text-[26px]" />
      </b-button>
      <h3 class="text-[#333333] text-[21px] text-center font-[500] mb-[24px]">
        Hubungkan Variasi Produk
      </h3>

      <MappingInfoCard
        :marketplace="marketplaceVariant"
        :kompack="kompackVariant"
        :is-synced="isSynced"
        :is-disconnecting="isDisconnectingVariant"
        @disconnect="handleVariantDisconnection"
      />

      <b-input-group class="mb-[12px]">
        <b-input-group-prepend is-text>
          <span class="k-search-normal-1" />
        </b-input-group-prepend>
        <b-form-input
          v-model="searchedVariant"
          type="search"
          placeholder="Cari produk"
          class="mb-[12px] !border-l-0"
          @input="handleSearchInput"
        />
      </b-input-group>

      <b-table
        :items="preparedVariants"
        :fields="variantsFields"
        class="rounded-[8px] border-[1px] border-[#E2E2E2]"
        responsive
        sticky-header
        show-empty
      >
        <template #empty>
          <div class="flex items-center justify-center h-full my-[12px] gap-[8px]">
            <b-spinner
              v-if="variantsState && variantsState.isLoading"
              small
              variant="primary"
            />
            <span class="text-[#828282] text-[14px] font-[400]">
              {{ tableEmptyText }}
            </span>
          </div>
        </template>

        <template #cell(action)="data">
          <BtnConnectVariant
            :ref="`btnConnectVariant-${data.index}`"
            :disabled="isSyncingVariant || usedVariants.has(data.item.variant_id)"
            :is-connected="data.item.is_connected"
            :is-synced="isSynced"
            @click="handleSyncVariant(data)"
          >
            {{ data.item.is_connected ? 'Terhubung' : 'Hubungkan' }}
          </BtnConnectVariant>
        </template>
      </b-table>
    </b-modal>

    <ModalConfirm
      ref="modalConfirm"
    />
  </div>
</template>

<script>
import { variantsFields } from './config'
import ModalConfirm from '../../ModalConfirm.vue'
import useVariantList from './composables/useVariantList'
import BtnConnectVariant from './BtnConnectVariant.vue'
import MappingInfoCard from '../../MappingInfoCard.vue'

export default {
  name: 'ModalSyncProduct',

  components: {
    ModalConfirm,
    BtnConnectVariant,
    MappingInfoCard,
  },

  data() {
    return {
      variantsFields,
      marketplaceVariant: {
        name: '',
        sku: '',
        id: '',
        parent_id: '',
      },
      searchedVariant: '',
      lastSearchedKeyword: '',
      searchDebounceId: null,
      variantsState: {
        data: [],
        isLoading: false,
        error: null,
      },
      kompackVariant: {
        name: '',
        sku: '',
        id: 0,
      },
      isSynced: false,
      shopId: '',
      index: null,
      isSyncingVariant: false,
      isDisconnectingVariant: false,
      productId: 0,
    }
  },

  computed: {
    preparedVariants() {
      return this.variantsState?.data
    },
    tableEmptyText() {
      if (!this.variantsState || this.variantsState.isLoading) {
        return 'Memuat data...'
      }
      if (this.searchedVariant) {
        return `Tidak ada data yang ditemukan untuk "${this.searchedVariant}"`
      }
      return 'Tidak ada data yang ditemukan'
    },
  },
  created() {
    this.initializeComposables()
    this.usedVariants = new Set()
  },
  beforeDestroy() {
    this.cleanup()
  },
  methods: {
    // initialize variant list composable
    initializeComposables() {
      const {
        fetchVariantsData, state: variantsState, handleSearchInput, performSearch, cleanupSearch,
      } = useVariantList(this)
      this.handleSearchInput = handleSearchInput
      this.performSearch = performSearch
      this.cleanupSearch = cleanupSearch

      this.variantsState = variantsState
      this.fetchVariantsData = fetchVariantsData
    },
    // cleanup search debounce
    cleanup() {
      if (this.cleanupSearch) {
        this.cleanupSearch()
      }
    },
    // reset state
    resetState() {
      this.marketplaceVariant = {
        name: '',
        sku: '',
        id: '',
        parent_id: '',
      }
      this.isSynced = false
      this.kompackVariant = {
        name: '',
        sku: '',
        id: 0,
      }
      this.shopId = ''
      this.index = null
      this.searchedVariant = ''
      this.productId = 0

      this.usedVariants.clear()
      if (this.cleanupSearch) {
        this.cleanupSearch()
      }
    },
    // Modal Control: Show Modal
    showModal() {
      this.$refs.modalSyncVariantKompack.show()
    },
    // Modal Control: Hide & Reset State
    closeModal() {
      this.isDisconnectingVariant = false
      this.$refs.modalSyncVariantKompack.hide()
      this.resetState()
    },
    // Data Management: Set Data for Variant Mapping
    async setData(data) {
      this.marketplaceVariant = {
        name: data.marketplace_variant_name,
        sku: data.marketplace_variant_sku,
        id: data.marketplace_variant_id,
        parent_id: data.id,
      }
      this.isSynced = data.is_synced
      this.kompackVariant = {
        name: data.variant_name,
        sku: data.variant_sku,
        id: data.variant_id,
      }
      this.shopId = data.shop_id
      this.index = data.index
      if (!data.is_synced) {
        this.usedVariants = new Set(data.usedVariants)
      }

      if (this.productId !== data.internal_product_id) {
        await this.fetchVariantsData({ product_id: data.internal_product_id })
        this.productId = data.internal_product_id
      }
    },
    async handleSyncVariant(data) {
      this.$refs[`btnConnectVariant-${data.index}`].setLoading(true)
      this.isSyncingVariant = true
      try {
        await this.$http_new.post(`/komship/api/v1/marketplace-product-variants/shops/${this.shopId}/sync`, {
          parent_id: this.marketplaceVariant.parent_id,
          variants: [{
            marketplace_variant_id: this.marketplaceVariant.id,
            variant_id: data.item.variant_id,
          }],
        })

        this.$emit('success', {
          type: 'sync',
          index: this.index,
          variant_id: data.item.variant_id,
          variant_name: data.item.variant_name,
        })
      } catch (error) {
        const isAlreadySynced = error.response?.data?.meta?.message.includes('already synced with another marketplace variant')
        this.$toast_error({
          message: isAlreadySynced
            ? 'Variasi produk ini sudah terhubung dengan variasi marketplace lain'
            : 'Gagal menghubungkan variasi produk dengan marketplace, silahkan coba lagi',
        })
      } finally {
        this.isSyncingVariant = false
        this.$refs[`btnConnectVariant-${data.index}`].setLoading(false)
      }
    },
    async handleVariantDisconnection(data) {
      this.isDisconnectingVariant = true

      this.$refs.modalConfirm.show({
        title: 'Apakah Kamu Yakin Akan Memutuskan Variasi Produk?',
        status: 'WARNING',
        description: 'Jika kamu memutuskan variasi produk ini, variasi produk tidak akan terhubung lagi dan Kamu tidak bisa melakukan transaksi pada variasi produk ini.',
        confirmLabel: 'Ya',
        cancelLabel: 'Batal',
        onConfirm: async () => {
          await this.$http_new.post(`/komship/api/v1/marketplace-product-variants/shops/${this.shopId}/unsync`, {
            parent_id: this.marketplaceVariant.parent_id,
            marketplace_variant_id: this.marketplaceVariant.id,
            variant_id: this.kompackVariant.id,
          })

          this.$refs.modalConfirm.show({
            status: 'SUCCESS',
            title: 'Variasi Produk Kamu Telah Terputus',
            description: 'Produk marketplace dan gudang kompack kamu sekarang sudah terputus. Kamu tidak bisa melakukan transaksi pada produk ini.',
            confirmLabel: 'Selesai',
            onConfirm: () => {
              this.$emit('success', {
                type: 'unSync',
                index: this.index,
              })

              this.$refs.modalConfirm.hide()
            },
          })
        },
        onCancel: () => {
          this.$refs.modalConfirm.hide()
          this.isDisconnectingVariant = false
        },
      })
    },
  },
}
</script>
